/* eslint-disable max-len */
import React from 'react';

// To set an example as advanced use the property `advanced: true`.
const examplesTree = {
    'Basic map initialization': {
        'Vector map': {
            page: 'vector-map.html',
            description: <p>Most basic usage of vector maps</p>
        },
        'Raster map': {
            page: 'raster-map.html',
            description: <p>
                The most basic and minimalistic example of the TomTom map usage. There are multiple possible ways
                of initializing the <a href="https://developer.tomtom.com/maps-sdk-web-js/documentation#Maps.Map"
                    target="_blank" rel="noopener noreferrer">map</a>.
                However, the only required parameter is the id of the DOM object where map is going to be displayed.
                It is important that the map object has a proper size.
            </p>
        },
        'Multiple maps': {
            page: 'multiple-maps.html',
            description: <p>
                Developers are not limited to attaching only a single map. You can put as many map objects as you want
                on the same page without any issues.
            </p>
        },
        'Copyrights and attribution': {
            page: 'copyrights.html',
            description: <p>
                There is a legal requirement to properly credit TomTom content display to the end users. When using
                the SDK proper clickable link is added to the map. This example shows how to customize the location
                of attribution control and its content by adding links to report map issue and information about privacy.
            </p>
        },
        'Static map image': {
            page: 'static-map-image.html',
            description: <p>
                This example shows usage of static map image service to show how to obtain an url to a desired part of the world map.
            </p>
        },
        'Geopolitical views': {
            page: 'geopolitical-views.html',
            description: <p>
                This example shows how to use Geopolitical views in the map. We also provide you with two maps
                side by side so that you can compare the different views available.
            </p>
        },
        'Base styles': {
            page: 'base-styles.html',
            description: <p>This example shows how to change the base layer, the style of the map and layers shown on the map.</p>
        }
    },
    'Map layers': {
        'Interactive POI layer': {
            page: 'interactive-poi-layer.html',
            description: <p>
                This example shows how to add interactivity to the POI layer.
                We are listening to clicks on the POI layer and replacing the clicked POI icon with our own layer to indicate selection.
            </p>
        },
        'GeoJSON polygon with solid fill': {
            page: 'geojson-overlay-solid-fill.html',
            description: <div>
                <p>
                    This example shows how to add a polygon overlay to the map. We are applying a solid fill with an outline.
                </p>
                <br />
                <p>
                    Please refer to <a href="https://developer.tomtom.com/maps-api/maps-api-documentation/map-style-specification" target="_blank" rel="noopener noreferrer">Map Style Specification</a> for detailed layer properties documentation.
                </p>
            </div>
        },
        'GeoJSON polygon with pattern fill': {
            page: 'geojson-overlay-pattern-fill.html',
            description: <div>
                <p>
                    This example shows how to add a polygon overlay to the map. We are applying a pattern fill to the polygon.
                </p>
                <br />
                <p>
                    Please refer to <a href="https://developer.tomtom.com/maps-api/maps-api-documentation/map-style-specification" target="_blank" rel="noopener noreferrer">Map Style Specification</a> for detailed layer properties documentation.
                </p>
            </div>
        },
        'WMS base layer': {
            page: 'wms-base-layer.html',
            description: <p>
                This example show the usage of <a href="https://developer.tomtom.com/maps-api/maps-api-documentation-raster/wms"
                    target="_blank" rel="noopener noreferrer"> WMS Service </a> as a provider for the base layer.
            </p>
        },
        'Layer with WMS source': {
            page: 'layer-wms-source.html',
            description: <p>
                This example shows how to add a layer with WMS source positioned before the first layer of the hybrid night style.
                <br />
                To read more about WMS please visit the <a href="https://developer.tomtom.com/maps-api/maps-api-documentation-raster/wms"
                    target="_blank" rel="noopener noreferrer">WMS Service documentation</a> page.
                <br />
                <p>
                    Please refer to <a href="https://developer.tomtom.com/maps-api/maps-api-documentation/map-style-specification" target="_blank" rel="noopener noreferrer">Map Style Specification</a> for detailed layer properties documentation.
                </p>
            </p>
        },
        'Setting custom map style': {
            page: 'setting-custom-map-style.html',
            description: <div>
                <p>
                    This example shows how to change map style "on the fly" using setStyle method.
                    You can call it with style schema, URL pointing to style, or style object.
                    Here we use style object provided by uploading file.
                    You can create your own custom style file using the <a rel="noopener noreferrer" target="_blank"
                        href="https://developer.tomtom.com/maps-api/map-styler">Map styler</a>.
                </p>
            </div>
        },
        'Changing layers visibility': {
            page: 'changing-layers-visibility.html',
            description: <div>
                <p>
                    This example shows how to change layer visibility on the map.
                    First, we get all map layers and then we add mechanism for toggling their visibility.
                </p>
            </div>
        },
        'Heatmap': {
            page: 'heatmap.html',
            description: <div>
                <p>This example shows how to add a heatmap layer on the map.</p>
                <br/>
                <p>
                    To build this example we created three clusters of points with different densities.
                    The areas with more points are shown as red in higher zoom levels.
                    <br/>
                    These points are then converted to a GeoJson object that is used to create the heatmap layer.
                </p>
                <br />
                <p>
                Please refer to <a href="https://developer.tomtom.com/maps-api/maps-api-documentation/map-style-specification" target="_blank" rel="noopener noreferrer">Map Style Specification</a> for detailed layer properties documentation.
                </p>
            </div>
        },
        'Building extrusions': {
            page: 'building-extrusions.html',
            description: <div>
                <p>On higher zoom levels, you can display 3D buildings. They are not visible by default.</p>
                <p>In this example, we are showing you two ways how to display or hide them.</p>
                <p>On the left map use On/Off button to switch the visibility.</p>
                <p>On the right map press right mouse button and pitch the map to switch the visibility.</p>
                <br />
                <p>
                    Please refer to <a href="https://developer.tomtom.com/maps-api/maps-api-documentation/map-style-specification" target="_blank" rel="noopener noreferrer">Map Style Specification</a> for detailed layer properties documentation.
                </p>
            </div>
        },
        'GeoJSON layer under labels': {
            page: 'layer-under-labels.html',
            description: <div>
                <p>
                    This example shows how to display a GeoJSON layer with polygons under labels. For more detailed instructions, please visit our <a
                        href="https://developer.tomtom.com/maps-sdk-web-js/tutorials-advanced/add-layer-between-map-layers" target="_blank"
                        rel="noopener noreferrer">Add a layer between map layers</a> tutorial.
                </p>
                <br />
                <p>
                    Please refer to <a href="https://developer.tomtom.com/maps-api/maps-api-documentation/map-style-specification" target="_blank" rel="noopener noreferrer">Map Style Specification</a> for detailed layer properties documentation.
                </p>
            </div>
        },
        'Bubble layer': {
            page: 'bubble-layer.html',
            description: <div>
                This example shows how to use data-driven styles on a BubbleLayer. Each bubble contains information about the congestion level percentage for given city. Data is based on <a
                    rel="noopener noreferrer" target="_blank" href='https://www.tomtom.com/en_gb/traffic-index'>TomTom Traffic Index</a>.
            </div>
        },
        'Styled road lines': {
            page: 'styled-road-lines.html',
            description: <div>
                <p>
                   This example presents how to draw lines on the map with different stylings.
                </p>
                <br />
                <p>
                    Please refer to <a href="https://developer.tomtom.com/maps-api/maps-api-documentation/map-style-specification" target="_blank" rel="noopener noreferrer">Map Style Specification</a> for detailed layer properties documentation.
                </p>
            </div>
        },
        'Image overlay': {
            page: 'image-overlay.html',
            description: <div>
                <p>
                    This example shows how to add a layer with image overlay on the map.
                </p>
                <p>
                    Please refer to <a href="https://developer.tomtom.com/maps-api/maps-api-documentation/map-style-specification" target="_blank" rel="noopener noreferrer">Map Style Specification</a> for detailed layer and source properties documentation.
                </p>
            </div>
        },
        'Video overlay': {
            page: 'video-overlay.html',
            description: <div>
                <p>
                    This example shows how to add a video as a layer on the map.
                </p>
                <br />
                <p>
                    Please refer to <a href="https://developer.tomtom.com/maps-api/maps-api-documentation/map-style-specification" target="_blank" rel="noopener noreferrer">Map Style Specification</a> for detailed layer properties documentation.
                </p>
            </div>
        }
    },
    'Animations': {
        'Animation of camera around a point': {
            page: 'animations-camera-around-point.html',
            description: <p>
                This example shows how to animate the map camera around a point.
            </p>
        },
        'Animated layer': {
            page: 'animated-layer.html',
            description: <div>
                <p>This example shows how to add animated layer based on CustomLayer interface.</p>
                <p>Please refer to <a rel="noopener noreferrer" target="_blank"
                    href="https://developer.tomtom.com/maps-sdk-web-js/documentation#ICustomLayer">Custom Layer interface documentation</a> for a list of available properties and methods.</p>
            </div>
        },
        'Animate icon along the path': {
            page: 'animate-icon.html',
            description: <div>
                <p>This example shows how to draw a path between two points and animate an icon along this path.</p>
            </div>
        }
    },
    'Map interaction': {
        'Changing map center': {
            page: 'changing-map-center.html',
            description: <p>
                A center point's latitude and longitude coordinates together with a zoom level can be passed to a method
                initializing a map object. You can construct the map only after a #map div is rendered - for example,
                by using the window.onload event or placing script just before body closing tag.
            </p>
        },
        'Automated location change': {
            page: 'automated-location-change.html',
            description: <p>
                Multiple map locations can be used in a form of an animated carousel. The only information that you need
                to provide is set of center points coordinates and a desired time interval between location switches.
                <br/>
                You have three options of location changing by deciding to use: <code>map.flyTo()</code>,
                <code>map.easeTo()</code> or <code>map.jumpTo()</code>. Play with them to choose the one that
                works best for you.
            </p>
        },
        'Resize map': {
            page: 'resize-map.html',
            description: <p>
                You can resize map size by changing map container size. Then you need to call <code>map.resize()</code>
                to inform library that viewport changed.
            </p>
        },
        'Map events': {
            page: 'map-events.html',
            description: <p>
                The map object can be combined with every JavaScript DOM event.
                For example, by using the click() event, application may get the clicked location's
                coordinates and show them in a popup.
            </p>
        },
        'Limit map interactions': {
            page: 'limit-map-interactions.html',
            description: <p>
                Visible map area can be limited to certain bounding box and a range of zoom levels. It is possible
                by setting the following properties: maxBounds, maxZoom and minZoom.
                Just test it by panning and zooming the map.
            </p>
        },
        'Block map interactions': {
            page: 'block-map-interactions.html',
            description: <p>
                There might be a need to disable particular map browsing capabilities for a given map instance.
                You can achieve this by using invidual options like <code>scrollZoom</code>, <code>dragPan</code>,
                as shown below or by setting <code>interactive</code> option to <code>false</code> to disable all
                map interactions.
            </p>
        },
        'Pan and zoom controls': {
            page: 'pan-and-zoom.html',
            description: <p>
                This example shows how custom pan and zoom controls can be added to the map.
                Please note that even though we include plugins from a remote location
                (see <code>script</code> and <code>style</code> tags in the <code>head</code>),
                you should not rely on the availability of these links, it is preferable to host them on your own server.
            </p>
        },
        'Pitch and bearing controls': {
            page: 'pitch-and-bearing.html',
            description: <p>
                Pitch and bearing controls to make map tilts and rotations a little bit easier.
            </p>
        },
        'Hover effect': {
            page: 'hover-effect.html',
            description: <p>
                Add hover effect to additional map layers like vector traffic incidents.
                In the example hovered element is highlighted and the popup window with additional information is displayed.<br /><br />
                Example implements solution for hovering over thin elements like traffic tubes.
                When using method <code>queryRenderedFeatures</code> (refer to <a href="https://api.tomtom.com/maps-sdk-for-web/6.x/6.6.0/documentation/dist/classes/Maps.Map.html#method_queryRenderedFeatures" target="_blank" rel="noopener noreferrer">documentation page</a>)
                provide bounding box instead of single point as geometry parameter. See the code below:<br /><br />
                <code>
                map.queryRenderedFeatures([<br />
                &#160;&#160;&#160;&#160;[mousePoint.x - bBoxSize, mousePoint.y - bBoxSize],<br />
                &#160;&#160;&#160;&#160;[mousePoint.x + bBoxSize, mousePoint.y + bBoxSize]<br />
                ], &#123;<br />
                &#160;&#160;&#160;&#160;layers: layersToQuery<br />
                &#125;);<br />
                </code>
            </p>
        },
        'Minimap': {
            page: 'minimap.html',
            description: <p>
                We can easily add a smaller map in any corner of the main map. It shows the main map in the context of
                lower zoom level.
            </p>
        },
        'Drawing tools': {
            page: 'drawing-tools.html',
            description: <div>
                <p>
                    This example shows how to use the DrawingTools plugin with our SDK.
                </p><br/>

                <b>Context menu</b>
                <p>When selecting a feature using the "select tool", a relevant context menu will appear above the feature.
                    The context menu offers controls depending on the selected feature type.</p><br/>
                <p>Available context menu controls:</p>
                <ul>
                    <li>fill control - allows to change the feature background</li>
                    <li>border control - allows to change the feature stroke line color</li>
                    <li>line width control - allows to change the feature stroke line width </li>
                    <li>corner amount control - allows to change the feature shape with specified amount of corners</li>
                    <li>delete control - deletes the selected feature</li>
                </ul>

                <b>Feature scaling/rotation</b><br/>
                <p>
                    A dedicated wrapper tool helps you make precise feature modification.</p>
                <p>When you select a feature, please do dblclick on it or press the "Edit" button
                    from the context menu, a wrapper appears around the feature.</p>
                <p>By dragging the wrapper points, the feature inside will be scaled.</p>
                <p>You can find the rotation button at the bottom right corner of the wrapper.</p><br/>
                <b>Available tools</b><br/>
                <ul>
                    <li>
            select<br/>
            - lets you select objects by clicking or dragging over them<br/>
            - lets you modify selected object by dragging its corners<br/>
            - depend on a feature type, above the feature appears a context menu<br/>
            - shortcut - V
                    </li>
                    <li>
            pen<br/>
            - lets you draw straight segments with great precision<br/>
            - to start drawing click on the map<br/>
            - to finish draw action do double click on the map, click on an initial point or press the ESC button<br/>
            - available context menu controls: fill, border, delete<br/>
            - shortcut - P
                    </li>
                    <li>
            line<br/>
            - allows you to draw a multipoint line on the map<br/>
            - to start drawing click on the map<br/>
            - to finish draw action do double click on the map or press the ESC button<br/>
            - available context menu controls: line width, border, delete<br/>
            - shortcut - L
                    </li>

                    <li>
            rectangle<br/>
            - allows you to draw rectangular shapes<br/>
            - to start drawing do mouse down on the map and drag it<br/>
            - to finish draw action do mouse up<br/>
            - available context menu controls: fill, border, delete<br/>
            - shortcut - R
                    </li>
                    <li>
            ellipse<br/>
            - allows you to draw elliptical shapes<br/>
            - to start drawing do mouse down on the map and drag it<br/>
            - to finish draw action do mouse up<br/>
            - available context menu controls: fill, border, delete<br/>
            - shortcut - E
                    </li>
                    <li>
            polygon<br/>
            - allows you to draw a feature with defined amount of corners<br/>
            - to start drawing do mouse down on the map and drag it<br/>
            - to finish draw action do mouse up<br/>
            - available context menu controls: fill, border, corner amount, delete<br/>
            - shortcut - Y
                    </li>
                </ul>
            </div>
        },
        'Distance measurement': {
            page: 'distance-measurement.html',
            description: <p>
                In this example we show you how to create a distance measurement tool with Maps SDK for Web and Turf.js.
            </p>
        },
        'Area measurement': {
            page: 'area-measurement.html',
            description: <div>
                <p>
                    In this example we show you how to create an area measurement tool with Maps SDK for Web, DrawingTools plugin and Turf.js.
                </p>
                <br />
                <p>
                    Please refer to <a href="https://developer.tomtom.com/maps-sdk-web-js/documentation#ttPlugins.DrawingTools" target="_blank" rel="noopener noreferrer">DrawingTools plugin documentation</a> for detailed information how to use it.
                </p>
            </div>
        }
    },
    'Markers': {
        'Draggable markers': {
            page: 'draggable-markers.html',
            description: <p>This example shows how to create a draggable marker.</p>
        },
        'Custom markers': {
            page: 'custom-markers.html',
            description: <p>The marker mechanism allows straight forward look and feel customizations.
                You can also embed multiple types of images, e.g. jpg, png, svg.</p>
        },
        'Markers clustering': {
            page: 'markers-clustering.html',
            description: <div>
                <p>
                    This example shows how to use and customize clustering layers using a predefined
                    set of points.
                </p>
                <br />
                <p>
                    Please refer to <a href="https://developer.tomtom.com/maps-api/maps-api-documentation/map-style-specification" target="_blank" rel="noopener noreferrer">Map Style Specification</a> for detailed layer and source properties documentation.
                </p>
            </div>
        },
        'Styled clusters': {
            page: 'styled-clusters.html',
            description: <div>
                This example shows how to cluster and style data with style definition.
            </div>
        }
    },
    'Traffic': {
        'Traffic Layers': {
            page: 'traffic-layers.html',
            description: <div>
                <p>This example shows how to control the visibility of traffic incidents and traffic flow tiles on the map.</p>
            </div>
        },
        'Vector traffic incidents': {
            page: 'vector-traffic-incidents.html',
            description: <div>
                <p>
                    This example shows how to display vector traffic incident tiles and incident details on the map.
                    Traffic Incident tiles can be displayed in three different variants:
                </p>
                <ul>
                    <li>day</li>
                    <li>dark</li>
                    <li>s0</li>
                    <li>s0-dark</li>
                </ul>
                <p>For more information please refer to the <a target='_blank' rel='noopener noreferrer' href='https://developer.tomtom.com/traffic-api/traffic-api-documentation/traffic-incidents'>
                    Traffic Incidents documentation</a>.
                </p>
            </div>
        },
        'Raster traffic incidents': {
            page: 'raster-traffic-incidents.html',
            description: <div>
                <p>
                    This example shows how to display raster traffic incident tiles and incident details on the map.
                    Traffic Incident tiles can be displayed in three different variants:
                </p>
                <ul>
                    <li>s1</li>
                    <li>s2</li>
                    <li>s3</li>
                </ul>
                <p>For more information please refer to the <a target='_blank' rel='noopener noreferrer' href='https://developer.tomtom.com/traffic-api/traffic-api-documentation/traffic-incidents'>
                    Traffic Incidents documentation</a>.
                </p>
            </div>
        },
        'Traffic incidents heatmap': {
            page: 'traffic-incidents-heatmap.html',
            description: <div>
                <p>This example shows how to add a heatmap layer to the map using traffic incidents.</p>
                <br />
                <p>
                    To build this example we start by rendering the map and fetch traffic incidents for the current viewport (bounding box).
                    Once we receive the response from the service, we extract all the points, generate a heatmap layer and add it to the map.
                </p>
            </div>
        },
        'Vector traffic flow': {
            page: 'vector-traffic-flow.html',
            description: <div>
                <p>This example shows vector traffic flow data from TomTom Traffic service.
                Traffic flow can be displayed in three different variants:</p>
                <ul>
                    <li>relative0 (default) - the speed relative to free-flow is taken into account, highlighting areas of congestion</li>
                    <li>relative0-dark - the dark variant of relative0</li>
                </ul>
            </div>
        },
        'Raster traffic flow': {
            page: 'raster-traffic-flow.html',
            description: <div>
                <p>This example shows rasterized data from TomTom Traffic service.
                Traffic flow can be displayed in three different variants:</p>
                <ul>
                    <li>absolute (default) - the colours reflect the absolute speed measured on the given road segment</li>
                    <li>relative - the speed relative to free-flow is taken into account, highlighting areas of congestion</li>
                    <li>relative-delay - displays relative speeds only where they are different from the freeflow speed (no
                    green segments)</li>
                </ul>
            </div>
        },
        'Traffic incidents list': {
            page: 'traffic-incidents-list.html',
            description: <div>
                <p>This advanced example shows how to use <a href="https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.incidentDetailsV5"
                    target="_blank" rel="noopener noreferrer">incidentDetailsV5 service</a> in order to get incidents data,
                    display incidents markers and popups.
                </p>

                <p>Incidents data is fetched when:</p>
                <ul>
                    <li>
                        incident tiles are updated. it happens every 20 seconds,
                        tiles are responsible for displaying incident tubes (segments of the road with jams).
                    </li>
                    <li>
                        user moves the map or zooms.
                    </li>
                </ul>

                <p>Generally, if you want to display traffic incidents markers we recommend using a style
                    with those markers already included like "incidents_day" or "incidents_night". To obtain a style URL,
                    please refer to <a href="https://developer.tomtom.com/maps-api/maps-api-documentation/map-styles"
                    target="_blank" rel="noopener noreferrer">Map Styles API documentation</a>. This approach will save
                    you API calls, is easier and sufficient in most use cases.
                </p>
            </div>
        },
        'Traffic flow segments': {
            page: 'traffic-flow-segments.html',
            description: <div>
                <p>This example shows vector traffic flow data from TomTom Traffic service. An individual traffic
                segment can be selected to get information on the speed and time of travel.
                Traffic flow can be displayed in three different variants:</p>
                <ul>
                    <li>relative0 (default) - the speed relative to free-flow is taken into account, highlighting areas of congestion</li>
                    <li>relative0-dark - the dark variant of relative0</li>
                </ul>
                <p>For more information please refer to the <a target='_blank' rel='noopener noreferrer' href='https://developer.tomtom.com/traffic-api/traffic-api-documentation-traffic-flow/flow-segment-data'>
                    Traffic Flow Segment Data documentation</a>.
                </p>
            </div>
        },
        'Custom incident markers and popups': {
            page: 'custom-incident-markers-and-popups.html',
            description: <div>
                <p>This example shows how to:</p>
                <ul>
                    <li>create custom incident markers</li>
                    <li>display customized popups</li>
                    <li>align the icon between both markers and popup</li>
                    <li>pair <a target='_blank' rel='noopener noreferrer'
                        href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.incidentDetailsV5'>
                        Traffic Incident Details </a>
                        informations with <a target='_blank' rel='noopener noreferrer'
                        href='https://developer.tomtom.com/traffic-api/traffic-api-documentation-traffic-incidents/vector-incident-tiles'>
                        Vector Traffic Incident Tiles</a>
                    </li>
                </ul>
            </div>
        },
        'Traffic information filtering': {
            page: 'traffic-information-filtering.html',
            description: <div>
                <p>
                    This example shows how you can filter traffic data coming from vector traffic tiles. Each tile has
                    an URL which can be modified. In this case we append "roadTypes" query parameter to it
                    which allows filtering visibility of traffic flow on certain road types.
                </p>
                <p>
                    Please refer to <a target='_blank' rel='noopener noreferrer' href='https://developer.tomtom.com/traffic-api/traffic-api-documentation-traffic-flow/vector-flow-tiles'>
                    Traffic API documentation</a> to obtain more information about traffic flow tiles and a list of parameters that you can modify.
                </p>
            </div>
        }
    },
    'Routing': {
        'Static route': {
            page: 'static-route.html',
            description: <div>
                <p>This example shows how to display static route based on the geoJSON data retrieved
                from the <a target='_blank' rel='noopener noreferrer' href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                Calculate Route</a> service.</p>

                <p>No traffic information is used to calculate routes.</p>
            </div>
        },
        'Routing A-B': {
            page: 'routing-ab.html',
            description: <div>
                This example shows how to create a basic routing application with the use of two SearchBoxes. This is where a user can enter chosen locations.
                It is also possible to calculate a route from current user's device location.
            </div>
        },
        'Static routes with popups': {
            page: 'static-routes-with-popups.html',
            description: <div>
                <p>This example shows how to display popups containing basic information about selected route.
                Routes are based on the geoJSON data retrieved from the <a target='_blank' rel='noopener noreferrer' href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                Calculate Route</a> service.</p>
                <p>No traffic information is used to calculate routes.</p>
            </div>
        },
        'Routing with draggable marker': {
            page: 'routing-with-draggable-marker.html',
            description: <div>
                <p>This example shows how to display a route based on the geoJSON data retrieved
                from the <a target='_blank' rel='noopener noreferrer' href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                Calculate Route</a> service.</p>
                <p>You can change the position of the markers by dragging them to any place.</p>
            </div>
        },
        'Routing with summary': {
            page: 'routing-with-summary.html',
            description: <div>
                <p>This example shows how to display a static route based on the GeoJSON data retrieved
                from the <a target='_blank' rel='noopener noreferrer' href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                Calculate Route</a> service with basic route information.</p>
            </div>
        },
        'Routing with raw instructions': {
            page: 'routing-with-raw-instructions.html',
            description: <div>
                <p>This example shows how to display a static route based on the geoJSON data retrieved
                from the <a target='_blank' rel='noopener noreferrer' href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                Calculate Route</a> service and allows to display the route guidance.</p>
            </div>
        },
        'Routing with instructions': {
            page: 'routing-with-instructions.html',
            description: <div>
                <p>This example shows how to display a route based on the geoJSON data retrieved
                from the <a target='_blank' rel='noopener noreferrer' href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                Calculate Route</a> service and allows to display route guidance.</p>
            </div>
        },
        'Waypoints': {
            page: 'waypoints.html',
            description: <p> In this example we will show you how to route through waypoints.</p>
        },
        'Supporting points': {
            page: 'supporting-points.html',
            description: <div>
                <p>
                    Supporting points are used for route reconstruction. They can also be used in combination with other POST
                    parameters as described <a rel="noopener noreferrer" target="_blank"
                        href="https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute">here</a>.
                    If you omit these parameters, the exact same route will be reconstructed. If you use minDeviationDistance and minDeviationTime, the
                    supporting points need to contain start point (as first supporting point) and end point (as last one).
                </p>
                <br />
                <p>
                    There are a few more considerations about supporting points:
                </p>
                <ul>
                    <li>You can't use it with waypoints.</li>
                    <li>The order of points is respected, when creating a route.</li>
                    <li>As opposed to waypoints, you can add as many supporting points as you want
                (maximum number of waypoints is 150).</li>
                </ul>
            </div>
        },
        'Units conversion': {
            page: 'units-conversion.html',
            description: <div>
                <p>Usage of measurement unit conversion.</p>
                <p>For more information
                please refer to <a target='_blank' rel='noopener noreferrer' href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                Calculate Route</a>.</p>
                <p>No traffic information is used to calculate routes.</p>
            </div>
        },
        'Batch routing': {
            page: 'batch-routing.html',
            description: <div>
                <p>This example shows how to use Batch Routing to calculate routes and estimate time for multiple departure times.
                </p>
                <p>For more information please refer to the <a target='_blank' rel='noopener noreferrer'
                    href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                Batch Routing</a> documentation.</p>
            </div>
        },
        'Matrix routing': {
            page: 'matrix-routing.html',
            description: <div>
                <p>The Matrix Routing service enables the calculation of a matrix of route summaries
                for a set of routes defined with origin and destination locations. For more information
                please refer to <a href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.matrixRouting' target="_blank" rel="noopener noreferrer">
                Matrix Routing</a> service.</p>
            </div>
        },
        'Reachable range': {
            page: 'reachable-range.html',
            description:
                <p>This example demonstrates the use of the <a target='_blank' rel='noopener noreferrer'
                    href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateReachableRange'>
                Calculate Reachable Range</a> service.
                This service returns among others a polygon representing the maximum distance that a vehicle
                could reach based on the consumption model values presented in the control panel. The reachable range
                area is then used to make a <a target='_blank' rel='noopener noreferrer' href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.geometrySearch'>
                Geometry Search</a> service call for either gas stations or electric vehicle charging
                points available inside that area.
                </p>
        },
        'Batch reachable range': {
            page: 'batch-reachable-range.html',
            description: <div>
                <p>This example shows how to use Reachable range to calculate the range for multiple locations
                    and timeBudgets with a single request to the Batch routing service.
                </p>
                <p>For more information please refer to the <a target='_blank' rel='noopener noreferrer'
                    href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateReachableRange'>
                Calculate reachable range</a> service documentation.</p>
            </div>
        },
        'Multi-stop EV routing': {
            page: 'multi-stop-ev-routing.html',
            description: <div>
                <p>This example shows how to use Long distance EV routing service to calculate route with stops for charging.
                </p>
                <p>For more information please refer to the <a target='_blank' rel='noopener noreferrer'
                    href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.longDistanceEVRouting'>
                Long distance EV routing</a> service documentation.</p>
            </div>
        }
    },
    'Routing options': {
        'Travel mode': {
            page: 'travel-mode.html',
            description: <div>
                <p>
                    User can select his mean of transportation for the planned route, which will have an impact on the resulting route.
                    This example does not use traffic information to calculate routes.
                </p>
                <p>For more information please refer to the <a target='_blank' rel='noopener noreferrer'
                    href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                Calculate Route</a> service.</p>
            </div>
        },
        'Route type': {
            page: 'route-type.html',
            description: <p>
                Routing may be parameterized by choosing a <a target='_blank' rel='noopener noreferrer'
                    href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>route type </a>
                from four types: fastest, shortest, eco, and thrilling. Choosing an option from the dropdown menu will redraw the route on a map.
            </p>
        },
        'Alternatives with deviation constraints': {
            page: 'alternatives-with-deviation-constraints.html',
            description: <div>
                <p>
                    This example demonstrates the usage of 'alternatives' and 'deviations' parameters in the <a target='_blank' rel='noopener noreferrer'
                        href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                    Calculate Route</a> service.</p>
                <p>
                    To find alternatives to the main route simply change the number of alternatives in the form to see them on the map.
                </p>
            </div>
        },
        'Include traffic': {
            page: 'include-traffic.html',
            description: <div>
                Routing results can be also affected by current road situations,
                there is an option for the routing engine to use current traffic incidents while calculating the route.
            </div>
        },
        'Avoid options': {
            page: 'avoid-options.html',
            description: <div>
                <p>Routing between locations may be determined by different avoid options using
                    the <a
                    rel="noopener noreferrer" target="_blank"
                    href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                    Calculate Route</a> service. Available options are: toll roads,
                    motorways, ferries, unpaved roads, carpools, already used roads.</p>
                <p>This example does not use traffic information to calculate routes.</p>
            </div>
        },
        'Allow/avoid vignettes': {
            page: 'routing-vignettes.html',
            description: <div>
                <p>This example shows the usage of 'allowVignette' and 'avoidVignette' parameters in
                    the <a target='_blank' rel='noopener noreferrer'
                    href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                    Calculate Route service</a>.
                </p>
                <p>These two methods have opposite effect on the calculated route, for instance,
                    if you use allowVignette with Hungary, it will be the same as using avoidVignette with
                    all the other Countries selected but Hungary.</p>
                <p>These methods have some constraints, they can not be used together and they expect to
                    receive at least one Country.</p>
                <p>This example does not use traffic information to calculate routes.</p>
            </div>
        },
        'Avoid areas': {
            page: 'avoid-areas.html',
            description: <div>
                This example demonstrates the <a target='_blank' rel='noopener noreferrer'
                    href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                avoidAreas </a>parameter. Use checkboxes to display areas which should be avoided when
                route is calculated. This example does not use traffic information to calculate routes.
            </div>
        },
        'Hilliness and windingness': {
            page: 'hilliness-and-windingness.html',
            description: <div>
                Routing may enter thrilling mode by adjusting two options: <a target='_blank' rel='noopener noreferrer'
                    href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                    windingness and/or hilliness</a>.
                This example does not use traffic information to calculate routes.
            </div>
        },
        'Arrival and departure time': {
            page: 'arrival-and-departure-time.html',
            description: <div>
                <a target='_blank' rel='noopener noreferrer'
                    href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                        Routing</a> may be parametrized by departing or arriving times. A user can leave from a given start location now
                or any time in the future. It is also possible to arrive at a given time in the future.
            </div>
        },
        'Truck parameters (basic)': {
            page: 'truck-parameters-basic.html',
            description: <div>
                <p>
                    The routing in truck mode is supported with additional parameters describing the vehicle's size, weight or load type.
                </p>
                <p>
                    For more information please refer to the <a target='_blank' rel='noopener noreferrer'
                        href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                    Calculate Route</a> service.
                </p>
                <p>
                    This example does not use traffic information to calculate routes.
                </p>
            </div>
        },
        'Truck size and weight': {
            page: 'truck-size-and-weight.html',
            description: <div>
                This example demonstrates the usage of vehicle parameters for <a target='_blank' rel='noopener noreferrer'
                    href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                        route calculation</a>. For more information on these parameteres please refer to the <a target='_blank' rel='noopener noreferrer'
                    href='https://developer.tomtom.com/routing-api/routing-api-documentation-routing/common-routing-parameters'> Common Routing API Parameters
                </a> documentation.
            </div>
        },
        'Truck load types': {
            page: 'truck-load-types.html',
            description: <div>
                <p>
                The routing in truck mode supports multiple values for load type parameters. Additionally different sets of values are supported either a route runs through a territory of USA or not.
                </p>
                <p>
                    For more information please refer to the <a target='_blank' rel='noopener noreferrer'
                        href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                    Calculate Route</a> service.
                </p>
            </div>
        },
        'Consumption models': {
            page: 'consumption-models.html',
            description: <div>
                <p>
                    This example demonstrates the usage of routing in conjunction with consumption model.
                </p>
                <p>
                    We have included 2 route scenarios: short and long route, and 4 vehicle types:
                </p>
                <ul>
                    <li>Light combustion car</li>
                    <li>Heavy combustion truck</li>
                    <li>Light electric car</li>
                    <li>Heavy electric car</li>
                </ul>
            </div>
        },
        'Route sections': {
            page: 'route-sections.html',
            description: <div>
                <p>This example demonstrates the usage of the sectionType parameter in the <a
                    rel="noopener noreferrer" target="_blank"
                    href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                Calculate Route</a> service.</p>
                <br/>
                <p>We have included 5 different scenarios using section types. In each scenario you will be able to see
                different sections of the route in a different color.</p>
                <br/>
                <p>Scenarios explanation:</p>
                <ul>
                    <li>Cross country: Uses the 'country' section which gives us the different country sections in the route;</li>
                    <li>Motorway and vignettes: In this scenario we present the 'tollVignette', 'motorway' and 'tollRoad'
                    sections of the route;</li>
                    <li>Cross shore: In this scenario we present 'tunnel' and 'ferry' sections of the route;</li>
                    <li>Park: In this scenario we combine car travel mode with the 'travelMode' and 'pedestrian' sections of the route.
                    Note that to obtain travelMode sections you need to specify a travel mode;</li>
                    <li>Traffic: In this scenario we use the 'traffic' section which provides us with the different traffic occurences
                    in the selected route;</li>
                </ul>

                <p>This example uses traffic information to calculate routes only when "Traffic" is chosen.</p>
            </div>
        },
        'Heading': {
            page: 'heading.html',
            description: <div>
                <p>This example shows you how to use the 'vehicleHeading' parameter. For more information please refer to
                    the <a
                    rel="noopener noreferrer" target="_blank"
                    href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                Calculate Route</a> service documentation.</p>
                <br/>
                <p>This example does not use traffic information to calculate routes.</p>
            </div>
        },
        'Max speed restriction': {
            page: 'max-speed-restriction.html',
            description: <div>
                VehicleMaxSpeed parameter in <a
                    rel="noopener noreferrer" target="_blank"
                    href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.calculateRoute'>
                Calculate Route</a> service specifies the maximum speed you want to travel with. For example,
                if you can drive with max speed set to 100 km/h, there is a higher chance that the routing service will lead you
                through the highways. But if your vehicle max speed is, for example 40 km/h, it will avoid highways.
            </div>
        }
    },
    'Search and geocoding': {
        'My location': {
            page: 'my-location.html',
            description: <p>
                By usage of HTML5 Geolocation API user location can be shown on the map.
                In this example Mapbox <a href="https://developer.tomtom.com/maps-sdk-web-js/documentation#Maps.GeolocateControl"
                    target="_blank" rel="noopener noreferrer"> GeolocateControl</a> is used to retrieve location and display UI button.
                Note that, geolocalization may be not available in user's device or permission can be denied
                and you need to handle those cases manually and display appropriate information to users.
            </p>
        },
        'Search': {
            page: 'search.html',
            description: <p>
                A simple application that shows how to use different Tomtom Search Services: such like:
                fuzzy search, point of interest search, category search, geometry search and nearby search.
                <br/><br/>
                Choose one of given search types from the list, type in the query that you want to search for
                (e.g. pizza, Amsterdam or New York) and press Submit button.
            </p>
        },
        'Geocode': {
            page: 'geocode.html',
            description: <p>
                This example shows you how to implement basic geocode search.
                If you need more information about Geocode check <a rel="noopener noreferrer" target="_blank"
                    href="https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.geocode">
                    Geocode service documentation
                </a>
            </p>
        },
        'Structured geocode': {
            page: 'structured-geocode.html',
            description: <p>
                A simple example that shows usage of structured geocode service to retrieve a list of results.
                Then results are displayed on the map and have interactive popups attached. For more information please
                check <a href="https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.structuredGeocode"
                    target="_blank" rel="noopener noreferrer">
                    structuredGeocode service documentation
                </a>.
            </p>
        },
        'Search with autocomplete': {
            page: 'search-with-autocomplete.html',
            description: <div>
                <p>This example shows how to use the SearchBox plugin with our SDK.</p>
                <ul>
                    <li>SearchBox combines autocomplete and fuzzySearch services responses in a single result list.</li>
                    <li>Autocomplete results are used as filters for the fuzzy search service calls.</li>
                    <li>Map center (lng, lat) is used for biasing search results.</li>
                </ul>
                <p>
                    For the list of all available options (eg. for optimizing the number of calls made to the API) visit: <a
                        rel="noopener noreferrer" target="_blank"
                        href="https://developer.tomtom.com/maps-sdk-web-js/documentation#ttPlugins.SearchBox">
                    SearchBox plugin documentation</a>.
                </p>
            </div>
        },
        'Search output parameters': {
            page: 'search-output-parameters.html',
            description: <p>This example visualizes the output of search calls. It prints out the whole output in a marker popup (
                click on a marker to see the response)</p>
        },
        'Search filtering options': {
            page: 'search-filtering-options.html',
            description: <p>
                This example shows how to use filters with <a
                    href="https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.fuzzySearch"
                    rel="noopener noreferrer" target="_blank">Fuzzy Search</a>.
                <br />
                <br />
                You have three different scenarios, each one uses one of the filters
                available for the search services,&nbsp;
                <strong>connectorSet</strong>, <strong>brandSet</strong> and <strong>categorySet</strong>.
                <br />
                <br />
                The service accepts more than one option for each filter type. Try combining filters from the dropdown
                to see how the response changes.
            </p>
        },
        'Entry points': {
            page: 'entry-points.html',
            description: <p>Example of search with entry points of results visualized.</p>
        },
        'Along route search': {
            page: 'along-route-search.html',
            description: <p>The Along Route Search endpoint allows you to perform a fuzzy search for POIs along a specified route.
                This search is constrained by specifying Detour Time limiting measure. The minimum number of route points is 2.</p>
        },
        'Polygons for search': {
            page: 'polygons-for-search.html',
            description: <p>
                This example shows how to use Additional Data service to retrieve polygons representing borders of a
                particular search result. Polygons are shown when geometry data is available.

                For more information please refer to the <a
                    rel="noopener noreferrer" target="_blank"
                    href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.additionalData'>
                    Additional Data</a> service documentation.
            </p>
        },
        'Batch Search': {
            page: 'batch-search.html',
            description: <p>
                A simple application that shows how to use Batch Search.

                For more information please refer to the
                <a href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.additionalData'
                    target="_blank" rel="noopener noreferrer"> Additional Data</a> service documentation.
            </p>
        },
        'Geopolitical views search': {
            page: 'geopolitical-views-search.html',
            description: <p>
                This example shows how search results may differ depending on the <a
                    href="https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.fuzzySearch"
                    rel="noopener noreferrer" target="_blank">Fuzzy Search</a> service view param,
                    which refers to the geopolitical view. Search results are presented as POIs on the map.
                Clicking on them reveals a popup with address that corresponds to a particular country.
                For the list of available views click on the link to the service shown above.
            </p>
        },
        'EV stations availability': {
            page: 'ev-stations.html',
            description: <p>
                This example shows how to use EV Charging Stations Availability service in order to get
                connectors data based on the charging station ID.
                For more information please refer to the <a
                    rel="noopener noreferrer" target="_blank"
                    href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.additionalData'>
                EV Charging Stations Availability</a> service documentation.
            </p>
        },
        'Place by ID search': {
            page: 'place-by-id.html',
            description: <p> This example shows how to use the <b>placeById</b> service, which provides detailed information
            about the Place found by its identifier. For more information please refer to the <a
                rel="noopener noreferrer" target="_blank"
                href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.placeById'>
            Place by ID</a> service documentation.</p>
        },
        'Related POIs': {
            page: 'related-pois.html',
            description: <p> This example shows how to use the <b>relatedPois</b> parameter, which provides ID's of related places.
            Related place can be either a child or parent of given POI. For more information please refer to the <a
                rel="noopener noreferrer" target="_blank"
                href='https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.fuzzySearch'>
            Fuzzy Search</a> service documentation.</p>
        }
    },
    'Reverse geocode': {
        'Reverse geocode': {
            page: 'reverse-geocode.html',
            description: <p>
            This basic example shows how to use the reverse geocode service to retrieve a result from a given position.
            Try clicking on any place on the map to get its address. For more information, please refer to
                <a href="https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.reverseGeocode"
                    target="_blank" rel="noopener noreferrer"> reverseGeocode service documentation</a>.
            </p>
        },
        'Reverse geocode (advanced)': {
            page: 'reverse-geocode-advanced.html',
            description: <p>
               This example shows how to use the reverse geocode service to retrieve a list of results from a given position.
               Try clicking on any place on the map to get its address. For more information, please refer to <a
                    href="https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.reverseGeocode"
                    target="_blank" rel="noopener noreferrer">
                    Reverse geocode service documentation</a>.</p>
        },
        'Cross street lookup': {
            page: 'cross-street-lookup.html',
            description: <p>
               This basic example shows how to use the cross street lookup service to a result from a given position.
               For more information, please refer to <a
                    href="https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.crossStreetLookup"
                    target="_blank" rel="noopener noreferrer">
                    CrossStreet lookup service documentation</a>.
            </p>
        },
        'Cross street lookup (advanced)': {
            page: 'cross-street-lookup-advanced.html',
            description: <p>
               This advanced example shows how to use the cross street lookup service with different options.
               You can try different combinations with the options available to see how they affect the result.
               For more information please refer to <a
                    href="https://developer.tomtom.com/maps-sdk-web-js/documentation#Services.services.crossStreetLookup"
                    target="_blank" rel="noopener noreferrer">
                    CrossStreet lookup service documentation</a>.
            </p>
        },
        'Entity type filtering': {
            page: 'entity-type-filtering.html',
            description: <p>
                Reverse geocoder parameter <code>entityType</code> allows to filter response by entity type.
                You can observe different output after selecting one or multiple entity types from the dropdown
                and clicking on the map.
            </p>
        },
        'Polygons for reverse geocoder results': {
            page: 'polygons-for-rev-geo-results.html',
            description: <p>
                Reverse geocoder parameter <code>entityType</code> allows to filter response by entity type.
                Additional data parameter <code>geometryZoom</code> allows you to adjust accurateness of the polygon.
                You can observe different output after selecting one or multiple entity types from the dropdown
                and clicking on the map.
            </p>
        }
    },
    'Miscellaneous': {
        'Check browser support': {
            page: 'check-browser-support.html',
            description: <p>
                This example shows how to check if the user's browser is supported
            </p>
        }
    }
};

export function findExample(wantedExamplePageUrl) {
    for (const categoryName in examplesTree) {
        if (examplesTree.hasOwnProperty(categoryName)) {
            for (const exampleName in examplesTree[categoryName]) {
                if (examplesTree[categoryName].hasOwnProperty(exampleName) &&
                    wantedExamplePageUrl === examplesTree[categoryName][exampleName].page) {
                    const example = examplesTree[categoryName][exampleName];
                    return {
                        name: exampleName,
                        page: example.page,
                        description: example.description,
                        category: categoryName,
                        advanced: example.advanced || false
                    };
                }
            }
        }
    }
    throw new Error(`Unknown example page url: ${wantedExamplePageUrl}`);
}

export function filterExamplesTree(wantedExamplesName) {
    const result = {};
    for (const mainCategory in examplesTree) {
        if (examplesTree.hasOwnProperty(mainCategory)) {
            for (const exampleName in examplesTree[mainCategory]) {
                if (examplesTree[mainCategory].hasOwnProperty(exampleName)) {
                    if (exampleName.toLowerCase().indexOf(wantedExamplesName) !== -1) {
                        result[exampleName] = examplesTree[mainCategory][exampleName];
                    }
                }
            }
        }
    }

    return result;
}

export default examplesTree;
